import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, X, Star, Clock, Zap, Image, Video, Film,
  Brain, Sparkles, Check, Crown, Coins, Edit3, Search, TrendingUp
} from 'lucide-react';
import { useModels } from '../hooks/useModels';
import { useCredits } from '../hooks/useCredits';
import { useState, useMemo } from 'react';

const GoogleLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const FluxLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path fill="rgba(255,255,255,0.80)" d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
  </svg>
);

const ChatGPTLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path fill="rgba(255,255,255,0.85)" d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142-.0852 4.783-2.7582a.7712.7712 0 0 0 .7806 0l5.8428 3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142.0852-4.7735 2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
  </svg>
);

const DefaultLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="16" rx="2" fill="rgba(255,255,255,0.15)"/>
    <path fill="rgba(255,255,255,0.55)" d="M8 8h8v2H8zM8 12h6v2H8zM8 16h4v2H8z"/>
  </svg>
);

const xAILogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path fill="rgba(255,255,255,0.90)" d="M13.55 11.06 20.5 2h-2.2l-6.04 7.4L7.1 2H1l7.32 10.64L1 22h2.2l6.4-7.85L15.1 22H21l-7.45-10.94zM10.55 13.08l-.74-1.06L3.84 3.26h2.54l4.75 6.8.74 1.06 6.18 8.84h-2.54l-5.96-8.88z"/>
  </svg>
);

const ZImageLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path fill="rgba(255,255,255,0.90)" d="M5 4h14v3L9.4 17H19v3H5v-3l9.6-10H5z"/>
  </svg>
);

const iconMap = {
  'Zap': Zap, 'Star': Star, 'Sparkles': Sparkles, 'Image': Image,
  'Video': Video, 'Film': Film, 'Brain': Brain, 'Crown': Crown, 'Edit3': Edit3, 'Search': Search
};

const subcategoryIconMap = {
  'Google': { icon: GoogleLogo },
  'Flux': { icon: FluxLogo },
  'OpenAI': { icon: ChatGPTLogo },
  'xAI': { icon: xAILogo },
  'Z-Image': { icon: ZImageLogo },
  'default': { icon: DefaultLogo }
};

const speedLabel = { fast: 'Veloce', medium: 'Medio', slow: 'Lento' };
const qualityLabel = { high: 'Alta', ultra: 'Ultra', premium: 'Premium' };

const QualityBadge = ({ quality }) => (
  <span className="pill-ghost px-2 py-0.5 rounded-lg text-xs font-medium" style={{ fontSize: 11 }}>
    {qualityLabel[quality] || 'Alta'}
  </span>
);

const SpeedBadge = ({ speed }) => (
  <span className="pill-ghost px-2 py-0.5 rounded-lg text-xs font-medium" style={{ fontSize: 11 }}>
    {speedLabel[speed] || 'Medio'}
  </span>
);

const ModelCard = ({ model, isSelected, canAfford, onSelect, category }) => {
  const ModelIcon = model.icon && iconMap[model.icon] ? iconMap[model.icon] : (category === 'image' ? Image : Video);

  return (
    <motion.div
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => canAfford && onSelect(model)}
      className={`relative p-5 rounded-2xl border transition-all duration-300 cursor-pointer flex flex-col h-full ${
        isSelected
          ? 'border-blue-500/50 shadow-[0_0_20px_rgba(59,130,246,0.15)]'
          : canAfford
          ? 'border-white/10 hover:border-white/20'
          : 'border-white/05 opacity-50 cursor-not-allowed'
      }`}
      style={{
        background: isSelected ? 'linear-gradient(145deg, rgba(59,130,246,0.1), rgba(139,92,246,0.05))' : 'rgba(255,255,255,0.02)',
        boxShadow: isSelected ? 'inset 0 1px 0 rgba(255,255,255,0.1)' : undefined,
      }}
    >
      {model.popular && (
        <div className="absolute -top-2.5 -right-2.5">
          <div className="pill-active px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
            <TrendingUp size={10} />
            Top
          </div>
        </div>
      )}

      {isSelected && (
        <div className="absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]">
          <Check size={14} style={{ color: '#fff' }} />
        </div>
      )}

      <div className={`icon-box w-12 h-12 rounded-xl mb-4 flex items-center justify-center ${isSelected ? 'primary' : ''}`}>
        <ModelIcon size={22} style={{ color: isSelected ? '#fff' : 'rgba(255,255,255,0.85)' }} />
      </div>

      <div className="mb-3 flex-grow">
        <h4 className="font-semibold text-white text-sm mb-1.5 leading-tight" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}>{model.name}</h4>
        <p className="text-xs leading-relaxed line-clamp-2" style={{ color: 'rgba(255,255,255,0.40)' }}>{model.description}</p>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        <QualityBadge quality={model.quality} />
        <SpeedBadge speed={model.speed} />
      </div>

      <div className="hidden md:block space-y-1.5 mb-3">
        {model.features?.slice(0, 2).map((feature, idx) => (
          <div key={idx} className="flex items-start gap-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
            <Check size={11} className="flex-shrink-0 mt-0.5" style={{ color: 'rgba(255,255,255,0.40)' }} />
            <span>{feature}</span>
          </div>
        ))}
      </div>

      <div className="text-xs space-y-1 mb-3" style={{ color: 'rgba(255,255,255,0.36)' }}>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1"><Clock size={11} /> Tempo</span>
          <span className="font-medium text-white/60">{model.avgTime}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1"><Star size={11} /> Rating</span>
          <span className="font-medium text-white/60">{model.rating}/5</span>
        </div>
        {category === 'image' && (
          <div className="flex items-center justify-between">
            <span>Risoluzione</span>
            <span className="font-medium text-white/60">{model.maxResolution}</span>
          </div>
        )}
        {category === 'video' && (
          <div className="flex items-center justify-between">
            <span>Durata max</span>
            <span className="font-medium text-white/60">{model.maxDuration}s</span>
          </div>
        )}
      </div>

      <div className="pt-3 mt-auto flex items-center justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <span className="flex items-center gap-1.5 font-semibold text-sm" style={{ color: canAfford ? 'rgba(255,255,255,0.80)' : 'rgba(255,80,80,0.80)' }}>
          <Coins size={13} />
          {model.credits} crediti
        </span>
        {!canAfford && (
          <span className="text-xs" style={{ color: 'rgba(255,80,80,0.70)' }}>Insufficienti</span>
        )}
      </div>
    </motion.div>
  );
};

export default function ModelPicker({ selectedModel, onModelSelect, category = 'image', className = '' }) {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const { imageModels, videoModels } = useModels();
  const { credits } = useCredits();

  const models = category === 'image' ? imageModels : videoModels;
  const currentModel = models?.find(m => m.id === selectedModel);

  const groupedModels = useMemo(() => {
    if (!models) return {};
    return models.reduce((acc, model) => {
      const subcat = model.subcategory || 'Altri';
      if (!acc[subcat]) acc[subcat] = [];
      acc[subcat].push(model);
      return acc;
    }, {});
  }, [models]);

  const subcategories = Object.keys(groupedModels);

  const filteredModels = useMemo(() => {
    if (!models) return [];
    if (searchQuery) {
      return models.filter(m =>
        m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (m.subcategory && m.subcategory.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    return groupedModels[activeTab] || [];
  }, [models, searchQuery, groupedModels, activeTab]);

  const handleModelSelect = (model) => {
    onModelSelect(model.id);
    handleCloseModal();
  };

  const canAffordModel = (model) => credits >= (model.credits || 0);

  const ModelIcon = currentModel?.icon && iconMap[currentModel.icon]
    ? iconMap[currentModel.icon]
    : (category === 'image' ? Image : Video);

  const handleOpenModal = () => {
    setShouldRender(true);
    setTimeout(() => setIsVisible(true), 10);
    if (subcategories.length > 0 && !activeTab) setActiveTab(subcategories[0]);
    setSearchQuery('');
  };

  const handleCloseModal = () => {
    setIsVisible(false);
    setTimeout(() => { setShouldRender(false); }, 300);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Trigger */}
      <motion.button
        type="button"
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        onClick={handleOpenModal}
        className="w-full flex items-center justify-between p-3.5 rounded-xl transition-all duration-300 panel hover:border-blue-500/30"
      >
        <div className="flex items-center gap-3">
          <div className="icon-box primary w-10 h-10 rounded-xl flex items-center justify-center shadow-lg">
            <ModelIcon size={18} style={{ color: '#fff' }} />
          </div>
          <div className="text-left">
            <div className="font-semibold text-white text-sm">
              {currentModel?.name || 'Seleziona Modello'}
            </div>
            {currentModel && (
              <div className="text-xs flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.38)' }}>
                <span>{currentModel.subcategory}</span>
                <span>·</span>
                <span className="flex items-center gap-0.5">
                  <Coins size={10} />
                  {currentModel.credits}
                </span>
              </div>
            )}
          </div>
        </div>
        <ChevronDown size={16} style={{ color: 'rgba(255,255,255,0.40)' }} />
      </motion.button>

      {/* Modal */}
      {shouldRender && createPortal(
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999,
            backgroundColor: `rgba(0,0,0,${isVisible ? 0.72 : 0})`,
            backdropFilter: isVisible ? 'blur(8px)' : 'blur(0px)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          onClick={handleCloseModal}
        >
          <div
            style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: `translate(-50%, -50%) scale(${isVisible ? 1 : 0.93})`,
              opacity: isVisible ? 1 : 0,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              width: '96vw', height: '90vh', maxWidth: '1160px', maxHeight: '860px',
              background: 'rgba(12,12,18,0.7)',
              border: '1px solid rgba(255,255,255,0.1)',
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              boxShadow: '0 40px 100px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.1)',
              borderRadius: 24,
            }}
            className="flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 pointer-events-none" style={{
              background: 'radial-gradient(circle at 50% -20%, rgba(59,130,246,0.15), transparent 60%)'
            }} />
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 sm:px-6 py-4 sm:py-5 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex items-center justify-between sm:flex-1">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.03em' }}>
                    Seleziona Modello
                  </h2>
                  <p className="text-xs sm:text-sm hidden sm:block" style={{ color: 'rgba(255,255,255,0.36)' }}>
                    Scegli il modello perfetto per la tua creazione
                  </p>
                </div>
                {/* Close button visible on mobile alongside title */}
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="sm:hidden p-2.5 rounded-xl transition-all duration-200 icon-box"
                >
                  <X size={18} style={{ color: 'rgba(255,255,255,0.65)' }} />
                </button>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                {/* Search */}
                <div className="relative flex-1 sm:flex-none">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgba(255,255,255,0.35)' }} />
                  <input
                    type="text"
                    placeholder="Cerca modelli..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-field w-full sm:w-56 pl-9 pr-4 py-2 sm:py-2.5 rounded-xl text-sm"
                  />
                </div>

                {/* Close button hidden on mobile (shown above) */}
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="hidden sm:flex p-2.5 rounded-xl transition-all duration-200 icon-box"
                >
                  <X size={18} style={{ color: 'rgba(255,255,255,0.65)' }} />
                </button>
              </div>
            </div>

            {/* Tabs */}
            {!searchQuery && (
              <div className="px-6 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex gap-1 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                  {subcategories.map((subcategory) => {
                    const subcatConfig = subcategoryIconMap[subcategory] || subcategoryIconMap['default'];
                    const SubcategoryIcon = subcatConfig.icon;
                    const isActive = activeTab === subcategory;

                    return (
                      <button
                        type="button"
                        key={subcategory}
                        onClick={() => setActiveTab(subcategory)}
                        className="relative flex items-center gap-2.5 px-5 py-4 whitespace-nowrap transition-all duration-200"
                        style={{
                          color: isActive ? '#fff' : 'rgba(255,255,255,0.42)',
                          fontFamily: 'var(--font-body)',
                          fontWeight: isActive ? 600 : 500,
                          fontSize: 14,
                        }}
                      >
                        {isActive && (
                          <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'rgba(255,255,255,0.80)' }} />
                        )}
                        <SubcategoryIcon size={16} />
                        <span>{subcategory}</span>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{
                            background: isActive ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)',
                            color: isActive ? 'rgba(255,255,255,0.80)' : 'rgba(255,255,255,0.35)',
                          }}
                        >
                          {groupedModels[subcategory]?.length || 0}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto modern-dark-scrollbar p-6">
              {searchQuery && (
                <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  {filteredModels.length} risultati per "<span className="text-white/60">{searchQuery}</span>"
                </p>
              )}

              {filteredModels.length === 0 && searchQuery ? (
                <div className="text-center py-20">
                  <Search size={40} className="mx-auto mb-4" style={{ color: 'rgba(255,255,255,0.18)' }} />
                  <p className="font-semibold text-white/50 mb-1">Nessun modello trovato</p>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.28)' }}>Prova con termini diversi</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredModels.map((model, index) => (
                    <motion.div
                      key={model.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index, 12) * 0.04, duration: 0.25 }}
                    >
                      <ModelCard
                        model={model}
                        isSelected={selectedModel === model.id}
                        canAfford={canAffordModel(model)}
                        onSelect={handleModelSelect}
                        category={category}
                      />
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
